import React, { ChangeEvent, FC, useState } from 'react';
import { Checkbox, Chip, Grid } from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteRenderOptionState,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
} from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CloseIcon from '@material-ui/icons/Close';

import { SharingChannelInfo, Network, useGetChatChannelsQuery } from 'api';
import { useNotify } from 'hooks';
import { PickerInput, PopupIcon } from './components';
import useAutoCompleteStyles from './styles/useAutoCompleteStyles';

interface IProps {
  selectedChannels: SharingChannelInfo[];
  onChannelChange: (channels: SharingChannelInfo[]) => void;
}

const ChatChannelPicker: FC<IProps> = ({ selectedChannels, onChannelChange }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const notify = useNotify();
  const autocompleteClasses = useAutoCompleteStyles();

  const { loading, data } = useGetChatChannelsQuery({
    onError: notify.mutationError,
    variables: { network: Network.Msteams },
  });

  const options = data?.getChatChannels?.msteams_channels ?? [];
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  function toggleOpen() {
    setOpen(!open);
  }

  function handleChange(event: ChangeEvent, nextValue: SharingChannelInfo[]) {
    setInputValue('');
    onChannelChange(nextValue);
  }

  function renderTags(value: SharingChannelInfo[], getTagProps: AutocompleteGetTagProps) {
    return value.map((channel, index) => (
      <Chip
        key={channel.team_id}
        label={channel.channel_name}
        deleteIcon={<CloseIcon />}
        {...getTagProps({ index })}
      />
    ));
  }

  function renderOption(option: SharingChannelInfo, { selected }: AutocompleteRenderOptionState) {
    return (
      <Grid container alignItems="center">
        <Grid item xs>
          {option.channel_name}
        </Grid>
        <Grid item>
          <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
        </Grid>
      </Grid>
    );
  }

  function renderInput(params: AutocompleteRenderInputParams) {
    return (
      <PickerInput
        working={loading}
        placeholder="components:chatChannelSuggest"
        onChange={setInputValue}
        params={params}
      />
    );
  }

  return (
    <Autocomplete
      multiple
      disableClearable
      disableCloseOnSelect
      open={open}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      classes={autocompleteClasses}
      options={options}
      loading={loading}
      onChange={handleChange}
      getOptionLabel={({ channel_name }) => channel_name}
      popupIcon={<PopupIcon open={open} />}
      renderTags={renderTags}
      renderOption={renderOption}
      renderInput={renderInput}
      value={selectedChannels}
      inputValue={inputValue}
    />
  );
};

export default ChatChannelPicker;
